import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import API from "../utils/api";
import { resolveAssetUrl } from "../utils/config";
import "../styles/portfolioDetail.css";

function PortfolioDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 로그인 정보 (AuthContext 없이 localStorage 기반)
  const userInfo = (() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo"));
    } catch {
      return null;
    }
  })();
  const isLoggedIn = !!userInfo;
  const isAuthor = userInfo && userInfo.name === portfolio?.author;

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);

    API.get(`/portfolio/${id}`)
      .then((res) => { if (!ignore) setPortfolio(res.data.content); })
      .catch(() => { if (!ignore) setError("포트폴리오를 불러오지 못했습니다."); })
      .finally(() => { if (!ignore) setLoading(false); });

    return () => { ignore = true; };
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm("정말로 삭제하시겠습니까?")) return;
    try {
      await API.delete(`/portfolio/${id}`);
      alert("포트폴리오가 삭제되었습니다.");
      navigate("/portfolio");
    } catch {
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  const handleEdit = () => navigate(`/portfolio/edit/${id}`, { state: { portfolio } });

  if (loading) {
    return (
      <div className="container section state-block">
        <div className="spinner" />
        로딩 중...
      </div>
    );
  }
  if (error) {
    return (
      <div className="container section state-block">
        <p className="state-block__title">{error}</p>
        <button className="btn btn--outline btn--sm" onClick={() => navigate("/portfolio")}>
          목록으로
        </button>
      </div>
    );
  }
  if (!portfolio) {
    return (
      <div className="container section state-block">
        <p className="state-block__title">포트폴리오를 찾을 수 없습니다.</p>
      </div>
    );
  }

  // 본문은 HTML(<b>,<br> 등)을 포함할 수 있어 sanitize 후 렌더한다.
  // 순수 텍스트면 줄바꿈을 <br>로 바꿔 가독성 유지.
  const rawContent = portfolio.content || "";
  const prepared = /<[a-z][\s\S]*>/i.test(rawContent) ? rawContent : rawContent.replace(/\n/g, "<br>");
  const contentHtml = DOMPurify.sanitize(prepared);

  return (
    <article className="portfolio-detail container container--narrow section">
      <div className="detail-topbar">
        <button className="btn btn--ghost btn--sm" onClick={() => navigate("/portfolio")}>
          ← 목록으로
        </button>
        {isLoggedIn && isAuthor && (
          <div className="detail-actions">
            <button className="btn btn--outline btn--sm" onClick={handleEdit}>수정</button>
            <button className="btn btn--danger btn--sm" onClick={handleDelete}>삭제</button>
          </div>
        )}
      </div>

      <header className="detail-header">
        {portfolio.type && <span className="badge">{portfolio.type}</span>}
        <h1 className="detail-title">{portfolio.title}</h1>
        <div className="detail-meta">
          <span>{portfolio.author}</span>
          <span>{new Date(portfolio.createdAt).toLocaleDateString()}</span>
          <span>조회 {portfolio.viewCount}</span>
        </div>
        {portfolio.url && (
          <a href={portfolio.url} target="_blank" rel="noopener noreferrer" className="detail-link">
            관련 링크 바로가기 ↗
          </a>
        )}
      </header>

      <div className="detail-content detail-content--rich" dangerouslySetInnerHTML={{ __html: contentHtml }} />

      {portfolio.images && portfolio.images.length > 0 && (
        <div className="detail-images">
          {portfolio.images.map((img, index) => (
            <img
              key={img?.id ?? index}
              src={resolveAssetUrl(typeof img === "object" ? img.url : img)}
              alt={`${portfolio.title} 이미지 ${index + 1}`}
              loading="lazy"
              className="detail-image"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ))}
        </div>
      )}
    </article>
  );
}

export default PortfolioDetail;
